import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { ArrowLeft, Save, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { onboardingAPI } from '../../lib/onboardingAPI';

export default function BusinessForm({ clientId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    business_name: '', abn: '', business_activity: '', trading_name: '',
    gst_registered: false, has_employees: false, accounting_software: '',
    bank_account_details: '', business_address: '',
  });

  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  useEffect(() => {
    if (!clientId) return;
    onboardingAPI.getSection(clientId, 'business_activity')
      .then((res) => setF((s) => ({ ...s, ...(res.data.response_data || {}) })))
      .catch(() => {}).finally(() => setLoading(false));
  }, [clientId]);

  const save = async (status) => {
    if (status === 'completed' && (!f.business_name.trim() || !f.business_activity.trim())) {
      toast.error('Business name and business activity are required');
      return;
    }
    setSaving(true);
    try {
      await onboardingAPI.saveSection(clientId, 'business_activity', { response_data: f, status });
      toast.success(status === 'completed' ? 'Business details saved' : 'Saved — you can return any time');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => navigate('/onboarding')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to checklist
      </Button>
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Business Activity</CardTitle>
          <CardDescription>Tell us about your sole-trader or business activity.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Business name *</Label><Input value={f.business_name} onChange={onInput('business_name')} data-testid="biz-name" /></div>
            <div className="space-y-2"><Label>Trading name</Label><Input value={f.trading_name} onChange={onInput('trading_name')} /></div>
            <div className="space-y-2"><Label>ABN</Label><Input value={f.abn} onChange={onInput('abn')} /></div>
            <div className="space-y-2"><Label>Business activity *</Label><Input value={f.business_activity} onChange={onInput('business_activity')} placeholder="e.g. plumbing, consulting" data-testid="biz-activity" /></div>
            <div className="space-y-2"><Label>Accounting software used</Label><Input value={f.accounting_software} onChange={onInput('accounting_software')} placeholder="e.g. Xero, MYOB" /></div>
            <div className="space-y-2"><Label>Business bank account details</Label><Input value={f.bank_account_details} onChange={onInput('bank_account_details')} /></div>
            <div className="space-y-2 md:col-span-2"><Label>Business address</Label><Input value={f.business_address} onChange={onInput('business_address')} /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[['gst_registered', 'GST registered'], ['has_employees', 'Has employees']].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 rounded-lg border p-3 cursor-pointer hover:bg-muted/50">
                <Checkbox checked={!!f[k]} onCheckedChange={(v) => set(k)(!!v)} data-testid={`biz-${k}`} />
                <span className="text-sm text-foreground/90">{label}</span>
              </label>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground/70">Financials (P&amp;L, balance sheet, bank statements, BAS, logbooks) will be requested in the Document Centre.</p>
          <div className="flex justify-between pt-2 border-t">
            <Button variant="outline" onClick={() => save('in_progress')} disabled={saving}><Save className="w-4 h-4 mr-2" /> Save &amp; return later</Button>
            <Button onClick={() => save('completed')} disabled={saving} data-testid="biz-complete"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as complete</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
